import React from 'react';
import {classNames} from '@utils/class-names';
import {ReactComponent as SearchIcon} from "@assets/icons/search.svg";
import {useSearchBar} from "@common/hooks/use-search-bar.hook";
import {useSpecialistApi} from "../../core/hooks/use-specialist-api";
import {XMarkIcon} from "@heroicons/react/20/solid";
import {SpecialistListResponse} from "../../core/models/interfaces/specialist-list-response.interface";
import AppButton from "@common/components/app-button";

interface SpecialistSearchbarProps {
    placeholder?: string;
    emitResults: (result: SpecialistListResponse, query: string) => void;
    onQueryChange?: (query: string) => void;
}

const SpecialistSearchbar: React.FC<SpecialistSearchbarProps> = (props: SpecialistSearchbarProps) => {
    const {placeholder = 'Szukaj', emitResults, onQueryChange} = props;
    const {getSearchSpecialists} = useSpecialistApi();
    const {searchQuery, setSearchQuery, clearSearch} = useSearchBar<SpecialistListResponse>(
        (searchQuery) => getSearchSpecialists(searchQuery, 20, 0),
        {
            debounceMs: 250,
            minChars: 0,
            onSuccess: (res) => emitResults(res ?? {specialists: [], totalCount: 0}, searchQuery),
            skipFirstLoading: true,
        }
    );

    const inputChangeHandler: React.ChangeEventHandler<HTMLInputElement> = (e): void => {
        setSearchQuery(e.target.value);
        onQueryChange?.(e.target.value);
    }

    const clearHandler = (): void => {
        clearSearch();
        onQueryChange?.("");
    }

    return (
        <div className="relative w-full">
            <SearchIcon
                className="absolute top-[10px] left-[15px] flex items-center pointer-events-none h-5 w-5 stroke-2"/>
            <input
                type="text"
                value={searchQuery}
                onChange={inputChangeHandler}
                placeholder={placeholder}
                className={classNames(
                    "w-full pl-12 pr-10 h-[40px]",
                    "bg-input-background/5 rounded-[4rem] font-normal",
                    "placeholder:text-on-surface text-base placeholder:text-base placeholder:font-normal",
                    "transition-colors duration-200 outline-primary/60",
                    "text-sm"
                )}
            />
            {searchQuery && (
                <AppButton
                    type="button"
                    onClick={clearHandler}
                    aria-label="Wyczyść wyszukiwanie"
                    className={classNames(
                        "!absolute !top-[6px] !right-[10px] !h-6 !w-6 !p-0",
                        "!bg-transparent !border-0 text-on-surface hover:text-primary"
                    )}
                    classNameContentWrapper="!p-0"
                >
                    <XMarkIcon className="h-5 w-5 stroke-2"/>
                </AppButton>
            )}
        </div>
    );
};

export default SpecialistSearchbar;
